// Human-friendly error messages
// Maps technical errors to calm, understandable language

import { getBackendBaseUrl } from './apiBase';

const isDev = process.env.NODE_ENV === 'development';

export const GOOGLE_SIGNIN_USER_MESSAGE =
  'We could not complete Google sign-in. Please try again, or use email sign-in for now.';

const UNREACHABLE_USER_MESSAGE =
  'We could not save this right now. Please check your connection and try again.';

const TIMEOUT_USER_MESSAGE =
  'This is taking longer than expected. Please check your connection and try again.';

/** Axios failed to get any HTTP response (backend down, wrong URL, CORS, or blocked). */
function logUnreachableApi(base) {
  if (isDev) {
    // eslint-disable-next-line no-console
    console.error(
      `[MyLegacyBridge] Cannot reach the API${base ? ` at ${base}` : ''}. ` +
        'Start the backend, confirm REACT_APP_BACKEND_URL in frontend/.env.development matches that URL, then restart npm start.',
    );
  } else {
    // eslint-disable-next-line no-console
    console.error(
      `[MyLegacyBridge] API unreachable${base ? ` (${base})` : ''}. ` +
        'Check network connectivity and that REACT_APP_BACKEND_URL was set when the frontend was built.',
    );
  }
}

function unreachableServerMessage(shouldLog = false) {
  if (shouldLog) {
    logUnreachableApi(getBackendBaseUrl());
  }
  return UNREACHABLE_USER_MESSAGE;
}

function timeoutMessage() {
  const base = getBackendBaseUrl();
  // eslint-disable-next-line no-console
  console.error(
    `[MyLegacyBridge] Request timed out${base ? ` waiting for ${base}` : ''}.`,
  );
  return TIMEOUT_USER_MESSAGE;
}

const ERROR_MESSAGES = {
  // Network errors (often means backend not running locally)
  'Network Error': UNREACHABLE_USER_MESSAGE,
  'ERR_NETWORK': UNREACHABLE_USER_MESSAGE,
  'ECONNREFUSED': UNREACHABLE_USER_MESSAGE,
  
  // Authentication errors
  '401': 'Please sign in to continue.',
  'Unauthorized': 'Your session has expired. Please sign in again.',
  'Invalid credentials': 'The email or password you entered is incorrect.',
  'Token expired': 'Your session has expired. Please sign in again.',
  'Google sign-in could not be verified': GOOGLE_SIGNIN_USER_MESSAGE,
  'We could not complete Google sign-in': GOOGLE_SIGNIN_USER_MESSAGE,
  'Google login is not configured': 'Google sign-in is not available right now. Please use email sign-in.',
  'Google account email is missing': 'Your Google account did not provide an email address.',
  'email is not verified': 'Your Google account email is not verified.',
  
  // Permission errors
  '403': 'You don\'t have permission to do this.',
  'Forbidden': 'You don\'t have permission to access this.',
  'Access denied': 'You don\'t have permission to access this.',
  
  // Not found errors
  '404': 'We couldn\'t find what you\'re looking for.',
  'Individual not found or not in this tree':
    'That family member could not be found in this archive. Refresh the page and try again.',
  'Individual not found': 'That family member could not be found in this archive.',
  'Person not found': 'This family member could not be found.',
  'Relationship not found': 'That family link could not be found. It may already have been removed.',
  'Voice note not found': 'That voice note could not be found.',
  'Not Found': 'We couldn\'t find what you\'re looking for.',
  'Tree not found': 'This family tree could not be found.',
  'Story not found': 'This story could not be found.',
  
  // Validation errors
  '400': 'Something doesn\'t look right. Please check your input.',
  'Bad Request': 'Please check your input and try again.',
  'Validation error': 'Some information appears to be missing or incorrect.',
  
  // Server errors
  '500': 'Something went wrong on our end. Please try again.',
  '502': 'Our servers are momentarily busy. Please try again.',
  '503': 'We\'re doing some maintenance. Please try again shortly.',
  'Internal Server Error': 'Something went wrong on our end. Please try again.',
  
  // Rate limiting
  '429': 'You\'re doing that too quickly. Please wait a moment.',
  'Too Many Requests': 'Please slow down and try again in a moment.',
  
  // File upload errors
  'File too large': 'This file is too large. Please try a smaller file.',
  'Invalid file type': 'This file type isn\'t supported.',
  'Upload failed': 'We couldn\'t upload your file. Please try again.',
  
  // AI/Processing errors
  'AI service not configured': 'AI features are temporarily unavailable.',
  'Transcription failed': 'We couldn\'t transcribe the audio. Please try again.',
  'Polish failed': 'We couldn\'t polish your story right now. Please try again.',
  
  // Generic fallback
  'default': 'Something went wrong. Please try again.'
};

/**
 * Converts technical error messages to human-friendly language
 * @param {Error|string|object} error - The error to humanize
 * @returns {string} Human-friendly error message
 */
export const humanizeError = (error) => {
  // HTTP responses (401/403/404/500, etc.) are not network failures.
  if (error?.response?.status) {
    const status = error.response.status;
    const detail = error.response.data?.detail;
    if (detail != null) {
      const d = detail;
      const errorKey = Array.isArray(d)
        ? d.map((x) => (typeof x === 'object' && x?.msg ? x.msg : String(x))).join(' ')
        : typeof d === 'object' && d !== null && typeof d.message === 'string'
          ? d.message
          : String(d);
      if (errorKey && errorKey.length < 160) {
        return errorKey;
      }
    }
    if (ERROR_MESSAGES[String(status)]) {
      return ERROR_MESSAGES[String(status)];
    }
  }

  // Axios: request was made but no response (connection refused, CORS, wrong URL, offline backend)
  if (error && typeof error === 'object' && !error.response && error.request) {
    return unreachableServerMessage(true);
  }

  if (error?.code === 'ECONNABORTED' || (typeof error?.message === 'string' && error.message.includes('timeout'))) {
    return timeoutMessage();
  }

  // Handle different error types
  let errorKey = '';
  
  if (typeof error === 'string') {
    errorKey = error;
  } else if (error?.response?.data?.detail != null) {
    const d = error.response.data.detail;
    if (typeof d === 'object' && d !== null && !Array.isArray(d) && typeof d.message === 'string') {
      errorKey = d.message;
    } else {
      errorKey = Array.isArray(d)
        ? d.map((x) => (typeof x === 'object' && x?.msg ? x.msg : String(x))).join(' ')
        : String(d);
    }
  } else if (error?.response?.status) {
    errorKey = String(error.response.status);
  } else if (error?.message) {
    errorKey = error.message;
  } else if (error?.code) {
    errorKey = error.code;
  }
  
  // Prefer explicit API detail when it is already human-readable (avoid vague "Not Found" overrides)
  if (
    errorKey &&
    errorKey.length < 160 &&
    !errorKey.includes('Exception') &&
    !/^Error:/i.test(errorKey)
  ) {
    const lower = errorKey.toLowerCase();
    const vagueOnly =
      lower === 'not found' ||
      lower === '404' ||
      lower.includes('this item may have been moved');
    if (!vagueOnly) {
      for (const [key, message] of Object.entries(ERROR_MESSAGES)) {
        if (key === 'default' || key === 'Not Found' || key === '404') continue;
        if (lower.includes(key.toLowerCase())) {
          return message;
        }
      }
      if (!errorKey.includes('_')) {
        return errorKey;
      }
    }
  }

  // Try to find a matching human message (generic fallbacks last)
  for (const [key, message] of Object.entries(ERROR_MESSAGES)) {
    if (key === 'default') continue;
    if (errorKey.toLowerCase().includes(key.toLowerCase()) ||
        key.toLowerCase().includes(errorKey.toLowerCase())) {
      return message;
    }
  }
  
  // If the error message is already human-readable (no technical jargon), use it
  if (errorKey && 
      !errorKey.includes('Error:') && 
      !errorKey.includes('Exception') &&
      !errorKey.includes('_') &&
      errorKey.length < 100) {
    return errorKey;
  }
  
  return ERROR_MESSAGES.default;
};

/**
 * Wrapper for toast.error that humanizes messages
 */
export const showError = (toast, error) => {
  const message = humanizeError(error);
  toast.error(message);
  return message;
};

/**
 * Standard error handler for async operations
 */
export const handleApiError = (error, toast, fallbackMessage) => {
  console.error('API Error:', error);
  const message = humanizeError(error) || fallbackMessage || ERROR_MESSAGES.default;
  if (toast) {
    toast.error(message);
  }
  return message;
};

export default {
  humanizeError,
  showError,
  handleApiError,
  ERROR_MESSAGES
};
